'use client'

import { Cell, Divider } from '@vinted/web-ui'
import { useEffect } from 'react'
import { validate as uuidvalidate } from 'uuid'

import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { cookiesDataByName, useCookie } from '@marketplace-web/environment/cookies-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { urlWithParams } from '@marketplace-web/shared/url-util'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'
import {
  clickEvent,
  userClickHelpCenterSearchResult,
  searchFaqEntriesEvent,
  HelpCenterQuestionModel,
} from '@marketplace-web/help-center/help-center-faq-entry-data'
import { AccessChannel } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

type Props = {
  questions: Array<HelpCenterQuestionModel>
}

const HelpCenterFaqLinks = ({ questions: faqLinksData }: Props) => {
  const { access_channel, search_text, thread_id } = useBrowserNavigation().searchParams
  const { track } = useTracking()
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const isHcSearch = access_channel === AccessChannel.HcSearch
  const cookies = useCookie()

  useEffect(() => {
    if (!isHcSearch) return
    if (typeof search_text !== 'string') return
    if (!helpCenterSessionId) return

    track(
      searchFaqEntriesEvent({
        searchSessionId: cookies.get(cookiesDataByName.help_center_search_session_id) || null,
        searchQuery: search_text,
        faqEntryIds: faqLinksData.map(question => question.id),
        helpCenterSessionId,
      }),
    )
  }, [track, faqLinksData, isHcSearch, search_text, helpCenterSessionId, cookies])

  const handleOnClick = (faqEntryId: number) => () => {
    if (!isHcSearch) return

    const targetDetails = {
      search_session_id: cookies.get(cookiesDataByName.help_center_search_session_id) || null,
      faq_entry_id: faqEntryId,
    }

    track(
      clickEvent({
        target: 'help_center_search_result',
        targetDetails: JSON.stringify(targetDetails),
      }),
    )

    if (!!helpCenterSessionId && uuidvalidate(helpCenterSessionId)) {
      track(
        userClickHelpCenterSearchResult({
          helpCenterSessionId,
        }),
      )
    }
  }

  const renderQuestionItem = ({ id, title, path }: HelpCenterQuestionModel) => {
    return (
      <Cell
        key={id}
        title={title}
        type={Cell.Type.Navigating}
        url={urlWithParams(path, {
          thread_id,
        })}
        onClick={handleOnClick(id)}
        chevron
        testId={`help-center-question-${id}`}
      />
    )
  }

  return (
    <SeparatedList separator={<Divider />}>{faqLinksData.map(renderQuestionItem)}</SeparatedList>
  )
}

export default HelpCenterFaqLinks
